import React, { useEffect, useMemo, useState } from 'react';
import {
  Link,
  useNavigate,
  useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  FormControl,
  LinearProgress,
  Paper,
  Radio,
  Select,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedChip as Chip, LocalizedInputLabel as InputLabel, LocalizedMenuItem as MenuItem, LocalizedTableCell as TableCell, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockIcon from '@mui/icons-material/Lock';
import { useAuth } from '../contexts/AuthContext';
import { useChart } from '../contexts/ChartContext';
import { hasPremiumSubscription } from '../utils/subscription';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';
const EVENT_OPTIONS = [
  ['marriage', 'Marriage'], ['childbirth', 'Childbirth'], ['firstJob', 'First Job / Service'],
  ['careerChange', 'Major Career Change'], ['business', 'Business Started'], ['education', 'Education Completion'],
  ['property', 'Property Purchase'], ['foreignTravel', 'Foreign Travel / Settlement'], ['relocation', 'Major Relocation'],
  ['illness', 'Major Illness / Surgery'], ['recovery', 'Recovery']
];
type LifeEvent = { type: string; date: string; precision: string };
const blankEvent = (type: string): LifeEvent => ({ type, date: '', precision: 'exactDate' });
const scoreColor = (score: number) => score >= 85 ? 'success' : score >= 70 ? 'primary' : score >= 55 ? 'warning' : 'default';
const formatOffset = (seconds: number) => {
  const sign = seconds >= 0 ? '+' : '-';
  const absolute = Math.abs(seconds);
  return `${sign}${Math.floor(absolute / 60)}m ${absolute % 60}s`;
};

const BirthTimeRectification: React.FC = () => {
  const { i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { currentChart, calculateChart, setCurrentChart } = useChart();
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const [birthDetails, setBirthDetails] = useState<any>(null);
  const [mode, setMode] = useState<'quick' | 'life' | 'advanced'>('life');
  const [rangeMinutes, setRangeMinutes] = useState(15);
  const [events, setEvents] = useState<LifeEvent[]>([blankEvent('marriage'), blankEvent('firstJob'), blankEvent('childbirth')]);
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [applyLoading, setApplyLoading] = useState<'update' | 'create' | null>(null);
  const [applyMessage, setApplyMessage] = useState<string | null>(null);
  const isPremium = hasPremiumSubscription(authState.user);
  const chartId = id === 'preview' ? currentChart?._id : id;

  useEffect(() => {
    document.title = 'AUTO BTR - KP Birth Time Rectification | Kundlistar';
    if (chartId) axios.get(`${API_BASE_URL}/charts/${chartId}`).then((response) => setBirthDetails(response.data.data.chart?.birthDetails)).catch(() => {});
    else setBirthDetails(currentChart?.birthDetails);
  }, [chartId, currentChart]);

  const validEventCount = useMemo(() => events.filter((event) => event.type && event.date).length, [events]);
  const updateEvent = (index: number, patch: Partial<LifeEvent>) => setEvents((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row));

  const runAutoBtr = async () => {
    if (mode !== 'quick' && validEventCount < 3) {
      setError('Add at least 3 dated life events. Five or more gives a more useful comparison.');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const response = await axios.post(`${API_BASE_URL}/charts/kp/auto-btr/analyze`, {
        chartId,
        chartData: chartId ? undefined : currentChart,
        mode,
        rangeMinutes,
        events: mode === 'quick' ? [] : events.filter((event) => event.type && event.date),
        language: (i18n.resolvedLanguage || i18n.language || 'en').split(/[-_]/)[0]
      });
      setResult(response.data.data);
      setSelectedCandidate(response.data.data.recommended);
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'AUTO BTR could not complete.');
    } finally {
      setLoading(false);
    }
  };

  const selectedBirthParts = () => {
    if (!selectedCandidate?.time) throw new Error('Select a BTR candidate time first.');
    const [dateOfBirth, clock] = String(selectedCandidate.time).split(' ');
    const [hour, minute, second] = clock.split(':').map(Number);
    return { dateOfBirth, timeOfBirth: { hour, minute, second } };
  };

  const applyCandidate = async (action: 'update' | 'create') => {
    if (!isPremium) return;
    const verb = action === 'update' ? 'update the current chart' : 'create a new BTR chart';
    if (!window.confirm(`Use ${selectedCandidate?.time?.split(' ')[1] || 'this time'} to ${verb}?`)) return;
    setApplyLoading(action);
    setApplyMessage(null);
    setError(null);
    try {
      const selected = selectedBirthParts();
      if (action === 'update') {
        if (!chartId) throw new Error('Save the current chart before updating it.');
        const response = await axios.post(`${API_BASE_URL}/charts/${chartId}/rectify`, { ...selected, calculationModule: 'kp' });
        setCurrentChart(response.data.data.chart);
        setApplyMessage('Current chart updated with the selected AUTO BTR time.');
      } else {
        const source = currentChart?.birthDetails || birthDetails;
        if (!source?.placeOfBirth) throw new Error('Complete birth details are required to create the BTR chart.');
        const created = await calculateChart({ name: source.name, ...selected, placeOfBirth: source.placeOfBirth, gender: source.gender, chartSettings: currentChart?.chartSettings || {}, calculationModule: 'kp', createdFrom: 'auto_btr', saveChart: true });
        const createdId = created?._id || created?.chartId;
        setApplyMessage(`New chart created as ${(created?.birthDetails?.name || `${source.name} BTR`)}.`);
        if (createdId) navigate(`/chart/${createdId}/kp`);
      }
    } catch (applyError: any) {
      setError(applyError.response?.data?.message || applyError.message || 'Could not apply the selected BTR time.');
    } finally {
      setApplyLoading(null);
    }
  };

  const recommended = result?.recommended;
  return (
    <Container maxWidth="xl" sx={{ py: 4, minHeight: '100vh' }}>
      <Paper sx={{ p: { xs: 2.5, md: 4 }, mb: 3, color: '#fff', background: 'linear-gradient(135deg,#16213e 0%,#6a1b9a 65%,#ad1457 100%)' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoFixHighIcon /><Typography variant="h3" fontWeight={800}>AUTO BTR</Typography>
              <Chip label="KP Engine" size="small" sx={{ bgcolor: 'rgba(255,255,255,.16)', color: '#fff' }} />
            </Stack>
            <Typography sx={{ mt: 1, opacity: .9 }}>Automated Birth Time Rectification using frozen Ruling Planets, KP cusps, life events and DBA verification.</Typography>
            <Typography variant="body2" sx={{ mt: .5, opacity: .72 }}>{birthDetails?.name || currentChart?.birthDetails?.name || 'Current chart'}</Typography>
          </Box>
          {id && <Button component={Link} to={`/chart/${id}/kp`} variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,.5)', alignSelf: 'center' }}>Back to KP</Button>}
        </Stack>
      </Paper>

      {!authState.isAuthenticated || !isPremium ? (
        <Alert severity="warning" icon={<LockIcon />} sx={{ mb: 3 }} action={<Button component={Link} to={authState.isAuthenticated ? '/upgrade' : '/login'} color="inherit">{authState.isAuthenticated ? 'Unlock Premium' : 'Login'}</Button>}>
          AUTO BTR is a premium professional module. Your inputs remain editable, but analysis requires an active Premium plan.
        </Alert>
      ) : null}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={800}>1. Rectification method</Typography>
        <ToggleButtonGroup exclusive value={mode} onChange={(_, value) => value && setMode(value)} sx={{ mt: 2, flexWrap: 'wrap' }}>
          <ToggleButton value="quick">Quick BTR</ToggleButton>
          <ToggleButton value="life">Life Event BTR</ToggleButton>
          <ToggleButton value="advanced">Advanced Professional</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {mode === 'quick' ? 'Ruling Planets and Ascendant Sign-Star-Sub linkage.' : mode === 'life' ? 'Ruling Planets plus weighted life-event promise and DBA verification.' : 'Full scoring with Four-Step chains and optional H1/H9 Origin validation.'}
        </Typography>
        <Typography variant="subtitle2" sx={{ mt: 3 }}>Recorded-time uncertainty</Typography>
        <ToggleButtonGroup exclusive value={rangeMinutes} onChange={(_, value) => value && setRangeMinutes(value)} size="small" sx={{ mt: 1 }}>
          {[5, 15, 30, 60].map((value) => <ToggleButton value={value} key={value}>±{value} min</ToggleButton>)}
        </ToggleButtonGroup>
      </Paper>

      {mode !== 'quick' && <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box><Typography variant="h6" fontWeight={800}>2. Verified life events</Typography><Typography variant="body2" color="text.secondary">Minimum 3; exact dates receive the strongest weight.</Typography></Box>
          <Button startIcon={<AddIcon />} onClick={() => setEvents((rows) => [...rows, blankEvent('property')])}>Add event</Button>
        </Stack>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {events.map((event, index) => <Stack key={index} direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <FormControl fullWidth><InputLabel>Event</InputLabel><Select label="Event" value={event.type} onChange={(e) => updateEvent(index, { type: e.target.value })}>{EVENT_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</Select></FormControl>
            <TextField fullWidth type="date" label="Event date" InputLabelProps={{ shrink: true }} value={event.date} onChange={(e) => updateEvent(index, { date: e.target.value })} />
            <FormControl fullWidth><InputLabel>Accuracy</InputLabel><Select label="Accuracy" value={event.precision} onChange={(e) => updateEvent(index, { precision: e.target.value })}><MenuItem value="exactDate">Exact date</MenuItem><MenuItem value="month">Month known</MenuItem><MenuItem value="year">Year known</MenuItem><MenuItem value="approximate">Approximate</MenuItem></Select></FormControl>
            <Button color="error" aria-label="Remove event" onClick={() => setEvents((rows) => rows.filter((_, rowIndex) => rowIndex !== index))}><DeleteOutlineIcon /></Button>
          </Stack>)}
        </Stack>
      </Paper>}

      <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <Button variant="contained" size="large" startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon />} disabled={loading || !authState.isAuthenticated || !isPremium || (!birthDetails && !currentChart)} onClick={runAutoBtr}>Start AUTO BTR</Button>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>The judgment chart is frozen when analysis starts. Candidate times are refined from minute to second resolution.</Typography>
      </Paper>
      {loading && <><LinearProgress /><Typography textAlign="center" color="text.secondary" sx={{ mt: 1 }}>Comparing KP candidate times…</Typography></>}
      {error && <Alert severity="error" sx={{ my: 3 }}>{error}</Alert>}

      {recommended && <Stack spacing={3}>
        <Paper sx={{ p: 3, border: '2px solid', borderColor: 'primary.main' }}>
          <Typography variant="overline" color="primary">Recommended birth time</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Box><Typography variant="h3" fontWeight={900}>{recommended.time.split(' ')[1]}</Typography><Typography color="text.secondary">{recommended.time.split(' ')[0]} · Correction {formatOffset(recommended.offsetSeconds)}</Typography></Box>
            <Box textAlign={{ md: 'right' }}><Chip color={scoreColor(recommended.score) as any} label={`${recommended.score}/100 · ${recommended.confidence}`} /><Typography variant="body2" sx={{ mt: 1 }}>Stable window: {result.stableWindow.from.split(' ')[1]} – {result.stableWindow.to.split(' ')[1]}</Typography></Box>
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>{Object.entries(recommended.components).map(([key, value]: any) => <Chip key={key} variant="outlined" label={`${key.replace(/([A-Z])/g, ' $1')}: ${value}%`} />)}</Stack>
        </Paper>

        <Paper sx={{ p: 3 }}><Typography variant="h6" fontWeight={800}>Why this time?</Typography>{result.why.map((reason: string) => <Typography key={reason} sx={{ mt: 1 }}>✓ {reason}</Typography>)}</Paper>

        <Paper sx={{ p: 3 }}><Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Life-event evidence</Typography><TableContainer><Table size="small"><TableHead><TableRow><TableCell>Event</TableCell><TableCell>Date</TableCell><TableCell align="right">Promise</TableCell><TableCell align="right">DBA</TableCell><TableCell align="right">Four-Step</TableCell><TableCell align="right">Score</TableCell></TableRow></TableHead><TableBody>{recommended.events.map((event: any) => <TableRow key={`${event.type}-${event.date}`}><TableCell>{event.label}</TableCell><TableCell>{event.date}</TableCell><TableCell align="right">{event.promiseScore}%</TableCell><TableCell align="right">{event.dashaScore}%</TableCell><TableCell align="right">{event.fourStepScore}%</TableCell><TableCell align="right"><Chip size="small" color={scoreColor(event.score) as any} label={`${event.score}%`} /></TableCell></TableRow>)}</TableBody></Table></TableContainer></Paper>

        <Paper sx={{ p: 3 }}><Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>Select BTR time</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select one candidate, then update this chart or create a separate Kundli with “BTR” added to its name.</Typography><TableContainer><Table size="small"><TableHead><TableRow><TableCell padding="checkbox">Select</TableCell><TableCell>Rank</TableCell><TableCell>Time</TableCell><TableCell>Correction</TableCell><TableCell>Ascendant chain</TableCell><TableCell align="right">Score</TableCell></TableRow></TableHead><TableBody>{[recommended, ...result.alternatives].map((candidate: any, index: number) => <TableRow hover selected={selectedCandidate?.time === candidate.time} key={candidate.time} onClick={() => setSelectedCandidate(candidate)} sx={{ cursor: 'pointer' }}><TableCell padding="checkbox"><Radio checked={selectedCandidate?.time === candidate.time} onChange={() => setSelectedCandidate(candidate)} /></TableCell><TableCell>#{index + 1}</TableCell><TableCell sx={{ fontFamily: 'monospace' }}>{candidate.time.split(' ')[1]}</TableCell><TableCell>{formatOffset(candidate.offsetSeconds)}</TableCell><TableCell>{candidate.ascendant.signLord} → {candidate.ascendant.starLord} → {candidate.ascendant.subLord}</TableCell><TableCell align="right">{candidate.score}</TableCell></TableRow>)}</TableBody></Table></TableContainer><Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}><Button variant="contained" disabled={!selectedCandidate || Boolean(applyLoading) || !chartId} onClick={() => applyCandidate('update')}>{applyLoading === 'update' ? 'Updating…' : 'Update current chart'}</Button><Button variant="outlined" disabled={!selectedCandidate || Boolean(applyLoading)} onClick={() => applyCandidate('create')}>{applyLoading === 'create' ? 'Creating…' : 'Create new Kundli — Name BTR'}</Button></Stack>{applyMessage && <Alert severity="success" sx={{ mt: 2 }}>{applyMessage}</Alert>}</Paper>
        <Alert severity="info">{result.disclaimer}</Alert>
      </Stack>}
    </Container>
  );
};

export default BirthTimeRectification;
